/** Confirms a save of the operator's default tag categories: the changes in words
 *  (`describeDiff`), who they reach, and a required reason (3–500 characters — the same
 *  rule and wording as the configuration save dialog), stored on the audit row and the
 *  revision.
 *
 *  A 409 (someone saved the defaults after this draft was started) keeps the dialog open,
 *  blocks Save and offers Reload — there is no merge: reloading drops this draft. Other
 *  failures stay in the dialog with the server's words. */
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import { useState } from 'react';

import { AppDialog } from '@/components/AppDialog';

export const REASON_MIN = 3;
export const REASON_MAX = 500;

export function SaveDefaultTagCategoriesDialog({
  open,
  lines,
  count,
  reach,
  reason,
  onReasonChange,
  saving,
  conflict,
  error,
  onReload,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  /** The changes, one line each ("Added ROOM", "MEAL: Check-in on"). */
  lines: readonly string[];
  count: number;
  /** Who gets the change, in words. */
  reach: string;
  reason: string;
  onReasonChange: (reason: string) => void;
  saving: boolean;
  /** The defaults changed on the server since the draft started (409). */
  conflict: boolean;
  error: string | null;
  onReload: () => void;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}) {
  const [touched, setTouched] = useState(false);
  const trimmed = reason.trim();
  const reasonValid = trimmed.length >= REASON_MIN;
  const canSubmit = !saving && !conflict && reasonValid && count > 0;
  const changes = `${count} ${count === 1 ? 'change' : 'changes'}`;

  const close = () => {
    if (saving) return;
    setTouched(false);
    onCancel();
  };
  const confirm = () => {
    setTouched(true);
    if (canSubmit) onConfirm(trimmed);
  };

  return (
    <AppDialog
      open={open}
      onClose={close}
      size="md"
      disableBackdropClose={saving}
      title="Save default tag categories"
      actions={
        <>
          <Button onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={confirm} disabled={!canSubmit}>
            {saving ? 'Saving…' : `Save ${changes}`}
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Typography variant="body2">
          {changes} — {reach}
        </Typography>
        <Box
          component="ul"
          aria-label="Changes"
          sx={{
            m: 0,
            py: 1,
            pl: 3,
            pr: 1.5,
            maxHeight: 240,
            overflowY: 'auto',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.default',
            typography: 'body2',
            '& li + li': { mt: 0.5 },
          }}
        >
          {lines.map((l, i) => (
            // Two categories may share a name; the position keeps the keys apart.
            <li key={`${i}:${l}`}>{l}</li>
          ))}
        </Box>
        <TextField
          label="Describe this change *"
          fullWidth
          multiline
          rows={2}
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          onBlur={() => setTouched(true)}
          helperText={
            touched && !reasonValid
              ? 'Required — at least 3 characters.'
              : 'Stored on the audit row so future operators can understand the intent.'
          }
          error={touched && !reasonValid}
          inputProps={{ maxLength: REASON_MAX }}
          disabled={saving || conflict}
        />
        {conflict && (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={onReload}>
                Reload…
              </Button>
            }
          >
            Someone else saved the default tag categories after you started editing, so saving now
            would overwrite their change. Reload to start again from their version — your changes
            will be dropped.
          </Alert>
        )}
        {error && <Alert severity="error">{error}</Alert>}
      </Box>
    </AppDialog>
  );
}
